/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

'use client';

import { memo, useCallback, useState, type ReactNode } from 'react';
import Stack from '@mui/material/Stack/Stack';

import {
  ActionChip,
  Icon,
  InputChip,
  Link,
  Skeleton,
  Typography,
} from '@marketplace/gather-ui/components';
import type { components } from '@marketplace/gather-api/types/marketplace-vehicle-catalog-mgmt';

import { useDeleteSearchHistory } from '@/rest/hooks/use-delete-search-history';
import {
  NotificationScope,
  NotificationType,
  useNotificationWithTimeout,
} from '@marketplace/gather-ui/providers/notification-provider';
import useParseSelectedFilters from './use-parse-selected-filters';

// TODO(randy): Does the design system tell us how ActionChips should look while loading
function LoadingSearchHistory(): ReactNode {
  return (
    <Skeleton preventDelay>
      <ActionChip disabled clickable={false} label='this is a fake label' />
    </Skeleton>
  );
}

const SEARCH_HISTORY_ERROR_MESSAGE =
  'We cannot delete your search record at this time. Please try again.';

type SearchHistory = components['schemas']['SearchHistory'];

export type SelectedFilters = components['schemas']['SelectedFilters'];

type SearchHistorySectionProps = {
  isPending: boolean;
  items: SearchHistory[];
  onSelect(query: string): void;
};

type SearchHistoryChipProps = {
  disabled: boolean;
  onSelect(query: string): void;
  query: string;
  searchHistoryId: string;
  selectedFilters: SelectedFilters;
};

const SearchHistoryChip = memo(function SearchHistoryChip({
  disabled,
  onSelect,
  query,
  searchHistoryId,
  selectedFilters,
}: SearchHistoryChipProps): ReactNode {
  const label = useParseSelectedFilters(query, selectedFilters);

  const handleSelect = useCallback(() => {
    onSelect(query);
  }, [query, onSelect]);

  // NOTE: This is meant to keep the chip disabled until the search history items
  // are refetched
  const [isDeleted, setIsDeleted] = useState(false);

  const { mutate, status: deleteStatus } = useDeleteSearchHistory();

  const notifyErrorWithTimeout = useNotificationWithTimeout({
    timeout: 3000,
    notifyArgs: [
      SEARCH_HISTORY_ERROR_MESSAGE,
      NotificationType.Error,
      true,
      NotificationScope.Page,
    ],
  });

  const handleDelete = useCallback(() => {
    mutate(searchHistoryId, {
      onSuccess() {
        setIsDeleted(true);
      },
      onError() {
        notifyErrorWithTimeout();
      },
    });
  }, [notifyErrorWithTimeout, mutate, searchHistoryId]);

  return (
    <InputChip
      clickable={deleteStatus === 'idle' || !disabled}
      deleteIcon={<Icon icon='closeSmall' />}
      disabled={deleteStatus === 'pending' || isDeleted || disabled}
      label={label}
      onClick={handleSelect}
      onDelete={handleDelete}
    />
  );
});

export const SearchHistorySection = memo(function SearchHistorySection({
  isPending,
  items,
  onSelect,
}: SearchHistorySectionProps): ReactNode {
  const { mutate, status: deleteAllStatus } = useDeleteSearchHistory();

  const notifyErrorWithTimeout = useNotificationWithTimeout({
    timeout: 3000,
    notifyArgs: [
      SEARCH_HISTORY_ERROR_MESSAGE,
      NotificationType.Error,
      true,
      NotificationScope.Page,
    ],
  });

  const onClearAll = useCallback(() => {
    mutate(undefined, {
      onError() {
        notifyErrorWithTimeout();
      },
    });
  }, [notifyErrorWithTimeout, mutate]);

  if (isPending) {
    return (
      <Stack alignItems='flex-start' direction='column' spacing={3}>
        <LoadingSearchHistory />
        <LoadingSearchHistory />
        <LoadingSearchHistory />
      </Stack>
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <Stack alignItems='center' direction='row' justifyContent='space-between'>
        <Typography color='text.primary' size='small' variant='functional'>
          Search History
        </Typography>
        <Link color='text.primary' component='button' variant='small' onClick={onClearAll}>
          Clear all
        </Link>
      </Stack>
      <Stack alignItems='flex-start' direction='column' spacing={3}>
        {items.map(({ searchHistoryId, query, selectedFilters }) => (
          <SearchHistoryChip
            disabled={deleteAllStatus === 'pending' || deleteAllStatus === 'success'}
            key={searchHistoryId}
            query={query}
            searchHistoryId={searchHistoryId}
            selectedFilters={selectedFilters}
            onSelect={onSelect}
          />
        ))}
      </Stack>
    </Stack>
  );
});
